<template>
  <!-- Batch writes: const batch = db.batch() --- write together and fail together
    batch.set() batch.set(), batch.commit()
   -->
  <v-layout v-if="freeCoupon" column justify-center align-center>
    <v-icon size="104" color="grey">mdi-ticket-percent-outline</v-icon>
    <p class="mt-8 display-1 font-weight-light text-center" style="max-width: 40%;">
      Your account is using a coupon code that doesn't allow you to view Billit's list of packages. <br> <br>
      Contact us at <a target="_blank" class="grey--text text--darken-4" href="mailto:hello@getbillit.com">hello@getbillit.com</a> if you would like to remove your account coupon
    </p>
  </v-layout>
  <v-container v-else-if="!loading" id="dashboard" fluid tag="section">
    <v-row>
      <v-col cols="12" lg="4" v-for="(item, index) in packages" :key="index">
        <base-material-chart-card color="billit">
          <template v-slot:image>
            <div></div>
          </template>

          <template v-slot:heading >
            <v-hover v-slot:default="{ hover }">
              <v-layout column class="py-5" style="cursor: pointer;" @click="currentPackage = item; previewDialog = true" v-ripple fill-height justify-center align-center>
                <v-icon :size="hover ? 46 : 40" style="transition: all 0.15s ease-in-out;">mdi-gift-outline</v-icon>
                <span class="caption font-weight-medium mt-2">Click to Preview</span>
              </v-layout>
            </v-hover>
          </template>
          <h4 class="card-title text-center font-weight-light mt-2 ml-2">{{ item.name }}</h4>
          <p class="text-center font-weight-light ml-2 mt-1">{{ item.packageType }} - {{ Object.keys(item.choices).length }} Choices.</p>
          <template v-slot:actions>
            <v-icon class="mr-1" small>mdi-clock-outline</v-icon>
            <span class="caption grey--text font-weight-light">Added {{ fromNow(item.created.toDate()) }}.</span>
            <v-spacer></v-spacer>
            <v-btn @click="currentPackage = item; installDialog = true" color="billit" class="font-weight-bold" rounded outlined :disabled="$store.state.practiceData.paused" >Install <v-icon small class="ml-1">mdi-plus-box-multiple-outline</v-icon></v-btn>
          </template>
        </base-material-chart-card>
      </v-col>
    </v-row>


    <v-dialog :persistent="installLoading" v-if="currentPackage" v-model="installDialog" max-width="500">
      <v-card>
        <v-card-title>
          Install {{ currentPackage.name }}
          <v-spacer />
          <v-icon aria-label="Close" @click="installDialog = false">mdi-close</v-icon>
        </v-card-title>

        <v-card-text class="body-1 text-center">
          <v-form>
            <v-layout align-center justify-center wrap class="mt-4">
              <v-scroll-x-transition>
                <v-select
                  multiple v-show="useLocations"
                  :items="$store.state.locations" :no-data-text="`No ${$store.getters.locL}s on record. Add one from the ${$store.getters.locU}s tab.`"
                  :disabled="allLocations" outlined v-model="locations"
                  :label="`Choose ${$store.getters.locU}s`"
                  item-text="name" item-value="id"
                >
                  <template v-slot:selection="{ item, index }">
                    <span style="width: 100%;" :class="{ 'caption': true, 'mt-3': index === 0 && locations.length > 1, 'mt-1': index > 0 && index < locations.length - 1 }"><v-icon color="billit darken-1">mdi-chevron-right</v-icon> {{ item.name }}</span>
                  </template>
                </v-select>
              </v-scroll-x-transition>
              <v-flex xs12 style="max-width: 416px;" class="mt-n10">
                <v-checkbox v-model="allLocations" :label="`All ${$store.getters.locU}s`" color="billit"></v-checkbox>
              </v-flex>            
            </v-layout>
            <!-- <v-text-field
              class="mt-5"
              append-icon="mdi-lock-outline"
              type="password"
              outlined dense v-model="accessCode"
              label="Verify Access Code"
            ></v-text-field> -->
          </v-form>

          <!-- prod todo: make this an expansion panel dropdown open by default as list could be long -->
          <v-layout column class="mb-9 mt-4 caption font-weight-light" align-center v-if="installedLocations.length > 0">
            <span>Note: This package is already installed in the following {{ $store.getters.locL }}s</span>
            <!-- style="width: 50%; max-width: 50%:" -->
            <p v-for="(loc, i) in installedLocations" :key="i" class="text-left my-0">– <span class="font-weight-medium" v-text="loc.name"></span></p>
          </v-layout>
          <v-btn
            class="font-weight-medium"
            color="billit" depressed default
            :loading="installLoading"
            rounded
            @click="installPackage"
          >Install</v-btn>
          <p v-if="feedback" class="mt-5 red--text">{{ feedback }}</p>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog scrollable v-if="currentPackage" v-model="previewDialog" max-width="600">
      <v-card>
        <v-card-title>
          Preview {{ currentPackage.name }}
          <v-spacer />
          <v-icon aria-label="Close" @click="previewDialog = false">mdi-close</v-icon>
        </v-card-title>

        <v-card-text class="body-1 text-center">
          <v-treeview v-if="currentPackage.choices" style="border: 1px solid #EEE; border-radius: 10px;" class="body-1 mb-10 font-weight-light" dense v-model="currentPackage.tree" :open="currentPackage.open" hoverable open-on-click :items="currentPackage.choices">
            <template v-slot:prepend="{ item, open }">
              <v-icon v-if="!item.file">{{ open ? 'mdi-folder-open-outline' : 'mdi-folder-network-outline' }}</v-icon>
              <v-icon v-else>{{ files[item.file] }}</v-icon>
            </template>
            <template v-slot:label="{ item }">
              <p class="my-0 ml-3 text-left" :title="item.name">{{ item.name }}</p>
            </template>
            <template v-slot:append="{ item }">
              <v-btn :disabled="!item.wysiwyg" @click.stop="rootLevel = false; currentItem = item; viewChildDialog = true" class="mr-0" icon>
                <v-icon>mdi-eye-outline</v-icon>
              </v-btn>
            </template>
          </v-treeview>

          <v-btn
            class="font-weight-medium"
            color="billit"
            depressed
            default
            rounded
            @click="installDialog = true"
          >Install</v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-if="currentItem" v-model="viewChildDialog" scrollable fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card>
        <v-toolbar style="max-height: 50px;" color="billit" dense dark>
          <v-icon size="large" class="mr-3">mdi-text-box-search-outline</v-icon>
          <v-toolbar-title class="font-weight-medium"> {{ currentItem.isEndpoint ? 'View Instruction' : 'View Step' }}</v-toolbar-title>
          <v-spacer />
          <v-icon size="28" aria-label="Close" @click="viewChildDialog = false">mdi-close</v-icon>
        </v-toolbar>
        <template v-if="currentItem.glance.length > 0">
          <v-layout row wrap justify-center align-center style="max-height: 200px;" class="mx-12 mt-6">
            <v-flex xs2 class="px-2 mb-5" v-for="(item, i) in currentItem.glance" :key="i">
              <GlanceBarCard :item="item" display/>
            </v-flex>
          </v-layout>
          <v-divider class="mb-4"></v-divider>
        </template>
        <div v-else class="my-6"></div>
        <div
          class="tiptap-vuetify-editor__content text-center"
          v-html="currentItem.wysiwyg"
        />
      </v-card>
    </v-dialog>
  </v-container>
  <div v-else-if="loading" style="height: 100vh;"></div>
  
</template>

<script>
import moment from 'moment'
import { db } from '@/firebase'
import { functions } from '@/firebase'

export default {
  data() {
    return {
      freeCoupon: false,
      currentPackage: null,
      currentItem: null,
      installedPackages: [],
      installDialog: false,
      previewDialog: false,
      allLocations: false,
      locations: [],
      feedback: '',
      loading: true,
      files: {
        redirect: "mdi-transfer-right",
        endpoint: "mdi-file-outline"
      },
      viewChildDialog: false,
      // accessCode: '',
      installLoading: false,
      packages: [],
    }
  },
  computed: {
    useLocations() {
      return !this.allLocations
    },
    installedLocations() {
      if (this.currentPackage) {
        // console.log(this.installedPackages.filter(c => c.packageId === this.currentPackage.id))
        let current = this.installedPackages.filter(c => c.packageId === this.currentPackage.id)
        if (current.filter(c => c.locations === 'all').length > 0) {
          return [{ name: 'All' }]
        } else {
          let locations = []
          current.forEach(c => {
            locations.push(...this.$store.state.locations.filter(l => c.locations.find(cl => cl.id === l.id)))
          })
          return [...new Set(locations)]
          // return this.$store.state.locations.filter(l => )
        }
      }
    }
  },
  methods: {
    fromNow(timestamp) {
      return moment(timestamp).fromNow()
    },
    installPackage() {
      this.feedback = ''
      this.installLoading = true
      let f = functions.httpsCallable('installPackage')
      // set to null if not being used
      f({ locations: this.useLocations ? this.locations : 'all', id: this.currentPackage.id }).then(result => {
        // this.accessCode = ''
        this.currentPackage = null
        this.installLoading = false
        this.installDialog = false
        if (this.$route.hash !== '#tutorial') this.$router.push('/')
      })
      .catch(error => {
        this.feedback = error.message
        this.installLoading = false
      })
    }
  },
  created() {
    Promise.all([
      this.$bind('packages', db.collection('packages')),
      this.$bind('installedPackages', db.collection('carriers').where('userId', '==', this.$store.state.user.uid).where('isPackage', '==', true))
    ])
    .then(() => {
      setTimeout(() => {
        this.packages.forEach(p => {
          p.tree = []
          p.open = []
        })
        this.loading = false
      }, 50)
    })
    .catch(err => {
      if (err.message === 'Missing or insufficient permissions.') {
        this.freeCoupon = true
        // alert("Your account is using a coupon code that doesn't allow you to view Billit's packages collection. \n \nContact us at hello@getbillit.com if you would like to remove your account coupon")
      }
    })
  },
}
</script>
